import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectPropertyById } from 'src/store/selectors/properties';
import { selectServiceRequestFormData } from 'src/store/selectors/serviceRequestForm';
import { selectSuiteById } from 'src/store/selectors/suites';
import {
  setServiceRequestFormData,
  setServiceRequestFormPreviousStep,
  setServiceRequestFormStep,
  exitServiceRequestForm,
} from 'src/store/actions/serviceRequestForm';
import { saveServiceRequest } from 'src/store/actions/serviceRequest';
import Button from '../Button';
import Col from '../Col';
import Diagnosis from '../Diagnosis/Diagnosis';
import File from '../File';
import Form from '../Form';
import Input from '../Input';
import Row from '../Row';
import Textarea from '../Textarea';
import TimeSlot from './TimeSlot';
import { upload } from 'src/helpers/upLoadHelper';
import { selectFaqByIssueType } from 'src/store/selectors/faqsNotesDocuments';
import { selectFormLoader } from 'src/store/selectors/FormLoader';
import Loader from '../Loader';
import Icon from '../Icon';
import Overlay from '../Overlay';
import CheckBox from '../../components/Checkbox';
import { convertImgToBase64 } from '../PropertyWalkThroughForm/IndexDB';
import { ALL_SLOTS, USER_ROLES } from 'src/common/constants';
import { variables } from 'src/Tenants';
import { onCheckDocument } from 'src/helpers/CommonHelpers';
import {
  ToastFail,
  onCheckUserCanAccessTheModule,
} from 'src/helpers/validationHelper';
import AnnotationImage from '../AnnotateImage/AnnotationImage';
import CreatableSelect from 'react-select/creatable';
import { fetchServiceRequestTags } from 'src/store/actions/formOptions';
import { ServiceRequestFormStepsProps } from 'src/components/ServiceRequestForm/ServiceRequestFormSteps';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import {
  ExtractRoleIdResult,
  safelyExtractUserRoleId,
  User,
} from 'src/utils/safelyExtractUserRoleId';
import { toast } from 'react-toastify';

interface ServiceRequestFormContentProps {
  serviceRequestFormData: any;
  property: any;
  suite: any;
  serviceRequestTags: any;
  theme: any;
  srAttachments: any;
  showTimeslots: any;
  onSetServiceRequestFormData: any;
  setTags: any;
  selectMultipleItems: any;
  handleDelete: any;
  tenantName: string;
  userRole: string;
}

const isNonShapeTenant = (tenantName: string) => {
  return tenantName !== 'Shape' && tenantName !== 'ShapeRC';
};

const isShapeTenantWithStaffRole = (tenantName: string, userRole: string) => {
  return (
    (tenantName === 'Shape' || tenantName === 'ShapeRC') &&
    userRole !== 'Home Owner' &&
    userRole !== 'Occupant'
  );
};

const ServiceRequestFormContent = ({
  serviceRequestFormData,
  property,
  suite,
  serviceRequestTags,
  theme,
  srAttachments,
  showTimeslots,
  onSetServiceRequestFormData,
  setTags,
  selectMultipleItems,
  handleDelete,
  tenantName,
  userRole,
}: ServiceRequestFormContentProps) => {
  const shouldShowTimeSlot = () => {
    return (
      showTimeslots &&
      (isNonShapeTenant(tenantName) ||
        isShapeTenantWithStaffRole(tenantName, userRole))
    );
  };

  return (
    <Row chonky>
      <Col chonky xs="12" md="6">
        <Diagnosis
          asset={serviceRequestFormData.asset_item_nameDescription}
          issueType={serviceRequestFormData.issueType}
          expiresAt={serviceRequestFormData.assetWarrantyExpiresAt}
          location={undefined}
          label={undefined}
        />
        <Row>
          <Col xs="12" md="6">
            <Input label="Property" value={property.name} disabled block />
          </Col>
          <Col xs="12" md="6">
            {serviceRequestFormData.IsServiceRequest &&
            serviceRequestFormData.IsServiceRequest === 'CommonArea' ? (
              <Input
                label="Common Area"
                value={serviceRequestFormData.CommonArea}
                disabled
                block
              />
            ) : (
              <Input
                label={variables.Suite}
                value={suite.number}
                disabled
                block
              />
            )}
          </Col>
        </Row>
        <Textarea
          required
          label="Description"
          placeholder="Add details so we can understand the issue better."
          onChange={(e: any) =>
            onSetServiceRequestFormData({ description: e.target.value })
          }
          value={serviceRequestFormData.description}
          block
        />
        {onCheckUserCanAccessTheModule([
          USER_ROLES.PROPERTY_DEVELOPER,
          USER_ROLES.ADMIN,
          USER_ROLES.SUPER_ADMIN,
          USER_ROLES.SERVICE_MANAGER,
          USER_ROLES.STRATA_MANAGER,
        ]) && (
          <>
            <label className="textarea__label">Tags</label>
            <CreatableSelect
              placeholder="Add or create tags"
              closeMenuOnSelect={false}
              isClearable
              isMulti
              options={
                serviceRequestTags.length > 0
                  ? serviceRequestTags.map((item) => ({
                      value: item.id,
                      label: item.name,
                    }))
                  : []
              }
              value={
                serviceRequestFormData.tags ? serviceRequestFormData.tags : []
              }
              onChange={(newValue: any, actionMeta: any) =>
                setTags(newValue, actionMeta)
              }
            />
          </>
        )}
      </Col>
      <Col chonky xs="12" md="6">
        <div className="mtsmall">
          <CheckBox
            label="Check the box to Annotate images uploaded"
            value={
              serviceRequestFormData.annotatateImage
                ? serviceRequestFormData.annotatateImage
                : false
            }
            onChange={() =>
              onSetServiceRequestFormData({
                annotatateImage: !serviceRequestFormData.annotatateImage,
              })
            }
          />
        </div>
        <File
          label="Attachment"
          onSelect={(file: any) =>
            onCheckDocument(file) && selectMultipleItems(file)
          }
          files={[]}
          type="image"
          multiSelect={true}
          required={theme === 'Shape' || theme === 'ShapeRC'}
        />
        {srAttachments.length > 0 &&
          srAttachments.map((data: any, i: any) => (
            <Row key={i}>
              <Col granola xs="12" md="9">
                {data.path}
              </Col>
              <Col granola xs="12" md="3">
                <div onClick={() => handleDelete(data, i)}>
                  <Icon glyph="times-circle" weight="light" size="small" />
                </div>
              </Col>
            </Row>
          ))}
        <br />
        {/* Homeowner Availability UI element. TODO: Refactor to use conditional theming and avoid hardcoding - https://app.asana.com/0/1208712340140532/1209850859782882/f */}
        {shouldShowTimeSlot() && (
          <TimeSlot
            handleSelectionFormData={(selectedState: any) =>
              onSetServiceRequestFormData({
                schedulePreferences: selectedState,
              })
            }
            serviceRequestForm
          />
        )}
      </Col>
    </Row>
  );
};

const ServiceRequestFormFooter = ({
  serviceRequestFormData,
  disableButton,
  theme,
  tenantName,
  userRole,
  onSave,
  loader,
  state,
  setState,
  onSaveImagesAnotated,
}) => {
  // Check if form submission should be disabled based on theme requirements.
  const isThemeBasedSubmitDisabled = (
    theme: any,
    serviceRequestFormData: any
  ): boolean => {
    // Check if theme is ShapeRC and attachments requirement.
    if (theme === 'ShapeRC' || theme === 'Shape') {
      const hasNoAttachments: boolean =
        !serviceRequestFormData.srAttachments ||
        !(serviceRequestFormData.srAttachments.length > 0);

      if (hasNoAttachments) {
        return true;
      }
    }

    // Not disabled based on theme conditions.
    return false;
  };

  const shouldDisableButtons = (): boolean => {
    // First check schedule preferences requirement.
    const isCommonAreaOrFormwerks: boolean =
      serviceRequestFormData.serviceRequestType === 'Common Area' ||
      localStorage.getItem('theme') === 'Formwerks';

    // If schedule preferences are required and not provided, disable the button, but only for non shape tanants!
    if (
      !isCommonAreaOrFormwerks &&
      !serviceRequestFormData.schedulePreferences &&
      isNonShapeTenant(tenantName)
    ) {
      return true;
    }

    // If it's a non-Shape tenant, don't disable.
    if (isNonShapeTenant(tenantName)) {
      return false;
    }

    // If it's a Shape tenant with staff role, don't disable.
    if (isShapeTenantWithStaffRole(tenantName, userRole)) {
      return false;
    }

    // At this point, we know it's a Shape tenant with a homeowner role - Disable only if no attachments.
    return isThemeBasedSubmitDisabled(theme, serviceRequestFormData);
  };

  return (
    <div
      style={{
        position: 'fixed',
        bottom: '0',
        left: '0',
        width: '100%',
        background: '#fff',
        padding: '15px',
        boxShadow: '0 0 0.3rem rgba(0, 0, 0, 0.3)',
      }}
    >
      <>
        {/* Render loaders if data is being processed. */}
        {loader && <Loader center={true} />}
        {serviceRequestFormData.showImageLoading &&
          serviceRequestFormData.showImageLoading === true && (
            <Loader center={true} />
          )}
        {/* Primary submit button. */}
        <Button
          color="teal"
          label={`Submit ${variables.serviceRequest}`}
          onClick={() => onSave('Save')}
          disabled={
            !serviceRequestFormData.description ||
            disableButton ||
            shouldDisableButtons()
          }
          block={undefined}
          outlined={undefined}
          icon={undefined}
          size={undefined}
          outline={undefined}
          weight={undefined}
          children={undefined}
          style={undefined}
        />
        {/* Secondary submit button to save and add another request. */}
        <Button
          color="teal"
          label="Save And Add Another"
          onClick={() => onSave('AddAnother')}
          disabled={
            !serviceRequestFormData.description ||
            disableButton ||
            shouldDisableButtons()
          }
          block={undefined}
          outlined={undefined}
          icon={undefined}
          size={undefined}
          outline={undefined}
          weight={undefined}
          children={undefined}
          style={undefined}
        />
        {/* Overlay for image annotation functionality. */}
        <Overlay
          showOverlay={state.showOverlay}
          content={
            <AnnotationImage
              onClose={() =>
                setState((prevState: any) => ({
                  ...prevState,
                  showOverlay: false,
                }))
              }
              images={serviceRequestFormData.srAttachments}
              onSaveImagesAnotated={(images: any) =>
                onSaveImagesAnotated(images)
              }
            />
          }
        />
      </>
    </div>
  );
};

// Main component that handles the service request form submission workflow.
const ServiceRequestFormDetails = (props: ServiceRequestFormStepsProps) => {
  // Component state for handling file uploads and form submission
  const [state, setState] = useState({
    files: '',
    showOverlay: false,
    file: '',
    disableButton: false,
  });

  const [userRole, setUserRole] = useState<string>();
  const [tenantName, setTenantName] = useState<string | null>(null);

  // Select relevant data from Redux store.
  const serviceRequestFormData = useSelector(selectServiceRequestFormData);
  const property = useSelector((state) =>
    selectPropertyById(state, serviceRequestFormData.property_id)
  );
  const suite = useSelector((state) =>
    selectSuiteById(state, serviceRequestFormData.suite_id)
  );
  const itemFaqCategories = useSelector((state) =>
    selectFaqByIssueType(state, serviceRequestFormData.issueType)
  );
  const loader = useSelector(selectFormLoader);
  const serviceRequestTags = useSelector(
    (state: any) => state.formOptions.serviceRequestTags
  );

  const loggedUser: User = useSelector(selectAuthenticatedUser);

  // Configure Redux action dispatchers.
  const dispatch = useDispatch();
  const onSetServiceRequestFormData = (data: any) =>
    dispatch(setServiceRequestFormData(data));
  const onSetServiceRequestFormPreviousStep = () =>
    dispatch(setServiceRequestFormPreviousStep());
  const onExitServiceRequestForm = () => dispatch(exitServiceRequestForm());
  const onSaveServiceRequest = (sr: any, type: any, from: any) =>
    dispatch(saveServiceRequest(sr, type, from));
  const onSetServiceRequestFormStep = (step: any) =>
    dispatch(setServiceRequestFormStep(step));
  const onFetchServiceRequestTags = () => dispatch(fetchServiceRequestTags());

  // Handle service request form submission. Todo - Refactor this to be cleaner: https://app.asana.com/0/1208712340140532/1209860456539132/f
  const onSave = async (type: any) => {
    setState((prevState: any) => ({ ...prevState, disableButton: true }));
    localStorage.removeItem('isAnnotationImageUpload');

    const { from } = props;

    let srAttachments =
      serviceRequestFormData.srAttachments &&
      serviceRequestFormData.srAttachments.length > 0
        ? serviceRequestFormData.srAttachments
        : [];

    let serviceRequestTagging = [];
    let tags =
      serviceRequestFormData.tags && serviceRequestFormData.tags.length > 0
        ? serviceRequestFormData.tags
        : [];

    if (tags.length > 0) {
      await Promise.all(
        tags.map((tag: any) => {
          if (tag.__isNew__) {
            serviceRequestTagging = [
              ...serviceRequestTagging,
              { name: tag.value },
            ];
          } else {
            serviceRequestTagging = [
              ...serviceRequestTagging,
              { id: tag.value },
            ];
          }
        })
      );
    }

    // Todo: TS issues need to be addressed here. This BOS code is brittle - https://app.asana.com/0/1208712340140532/1209860456539132/f
    let attachments: any = [];

    if (srAttachments && srAttachments.length > 0) {
      onSetServiceRequestFormData({ showImageLoading: true });
      for (var i = 0; i < srAttachments.length; i++) {
        if (srAttachments[i].preview) {
          const uploadedURL = await upload(srAttachments[i]);

          if (!uploadedURL) {
            ToastFail(
              `Images were not successfully uploaded for the ${variables.serviceRequest.toLowerCase()}. Please try again. If this problem persists, kindly reach out to our support team for assistance.`
            );
            return;
          }

          attachments = [...attachments, uploadedURL];
        } else {
          attachments = [...attachments, srAttachments[i].url];
        }
      }

      onSetServiceRequestFormData({
        showImageLoading: false,
      });
      attachments = JSON.stringify(attachments).toString();
    } else {
      onSetServiceRequestFormData({
        showImageLoading: false,
      });
      attachments = '';
    }

    /* Todo: This code from BOS isn't great. We should refactor this to avoid reassigning objects by reference like this.
     * https://app.asana.com/0/1208712340140532/1209860456539132/f */
    let sr: any = serviceRequestFormData;
    sr = {
      ...serviceRequestFormData,
      Attachment: attachments,
      subject: 'Service Request',
      StatusName: 'Submitted',
      tags: serviceRequestTagging,
    };

    if (sr && sr.IsServiceRequest && sr.IsServiceRequest === 'CommonArea') {
      sr = { ...sr, schedulePreferences: ALL_SLOTS };
      const { suiteId, suite_id, ...srData } = sr;
      onSetServiceRequestFormData({
        showImageLoading: true,
      });
      onSaveServiceRequest(srData, type, from);
    } else {
      onSetServiceRequestFormData({
        showImageLoading: true,
      });
      onSaveServiceRequest(sr, type, from);
    }
  };

  // Convert uploaded files to base64 format.
  const upLoadImage = async (files: any) => {
    let upLoadedImages: any[] = [];
    for (var i = 0; i < files.length; i++) {
      let data = await convertImgToBase64(files[i]);

      if (data) {
        upLoadedImages.push({ path: files[i].path, url: data });
      } else {
        ToastFail(`failed to read the ${files[i].path} please try again`);
      }
    }

    return upLoadedImages;
  };

  // Handle selection of multiple image attachments.
  const selectMultipleItems = async (files: any) => {
    let srAttachments: any =
      serviceRequestFormData.srAttachments &&
      serviceRequestFormData.srAttachments.length > 0
        ? serviceRequestFormData.srAttachments
        : [];
    if (files && files.length > 0) {
      if (
        serviceRequestFormData.annotatateImage &&
        serviceRequestFormData.annotatateImage === true
      ) {
        onSetServiceRequestFormData({ showImageLoading: true });
        let convertedImages = await upLoadImage(files);
        onSetServiceRequestFormData({
          srAttachments: [...convertedImages, ...srAttachments],
          showImageLoading: false,
        });
        if (convertedImages && convertedImages.length > 0) {
          setState((prevState) => ({ ...prevState, showOverlay: true }));
        }
      } else {
        onSetServiceRequestFormData({
          srAttachments: [...files, ...srAttachments],
          showImageLoading: false,
        });
      }
    }
  };

  // Handle deletion of uploaded attachments.
  const handleDelete = (item: any, i: any) => {
    let srAttachments =
      serviceRequestFormData.srAttachments &&
      serviceRequestFormData.srAttachments.length > 0
        ? serviceRequestFormData.srAttachments
        : [];
    srAttachments = srAttachments.filter(
      (data: any, index: any) => i !== index
    );
    onSetServiceRequestFormData({ srAttachments: srAttachments });
  };

  // Handle saving of annotated images.
  const onSaveImagesAnotated = (images: any) => {
    onSetServiceRequestFormData({ srAttachments: images });
    setState((prevState) => ({ ...prevState, showOverlay: false }));
  };

  // Handle selection of service request tags.
  const setTags = (newValue: any) => {
    onSetServiceRequestFormData({
      tags: newValue,
    });
  };

  // Clear any previous state
  const clearPreviousState = () => {
    localStorage.removeItem('isAnnotationImageUpload');
    onSetServiceRequestFormData({
      uploadedImages: [],
      showImageLoading: false,
    });
  };

  // Initialize form based on tenant theme
  const initializeFormByTheme = () => {
    const theme: string = localStorage.getItem('theme');

    if (theme === 'Rize' || theme === 'Formwerks') {
      onSetServiceRequestFormData({
        schedulePreferences: 'Monday',
      });
    }

    return theme;
  };

  /* Ideally, the safe extraction logic of the role ID shouldn't live in the component
   * and should be sorted out further up in context or the store where the data is sourced.
   * Complex data extraction and validation logic should be handled upstream (in context, store, or data layer)
   * keeping the component as dumb as possible and always receiving the correct version
   * Todo: Refactor this later - https://app.asana.com/0/0/1209860455960017 */
  const setupUserRole = () => {
    const { data: roleId, error: roleError }: ExtractRoleIdResult<string> =
      safelyExtractUserRoleId(loggedUser);

    if (roleError) {
      console.error('Error extracting role ID:', roleError.message); // For Appsignal.
      toast.error(
        'Unable to verify your permissions. This may be a temporary issue or your account may need additional access. ' +
          'Please try refreshing the page or contact Juniper Support at support@juniper.re for assistance.',
        {
          position: 'top-center',
        }
      );
      return false;
    }

    if (roleId) {
      setUserRole(roleId);
    }

    return true;
  };

  useEffect(() => {
    // Main initialization function
    const initializeComponent = () => {
      clearPreviousState();
      const theme: string = initializeFormByTheme();
      onFetchServiceRequestTags();
      const roleSetupSuccessful: boolean = setupUserRole();
      if (!roleSetupSuccessful) return;
      setTenantName(theme);
    };

    initializeComponent();
  }, []);

  // Extract props and compute derived state.
  const { onExit } = props;
  const theme: string = localStorage.getItem('theme');
  const srAttachments: any =
    serviceRequestFormData.srAttachments &&
    serviceRequestFormData.srAttachments.length > 0
      ? serviceRequestFormData.srAttachments
      : [];
  const showTimeslots: boolean = !(
    theme === 'Rize' ||
    theme === 'Formwerks' ||
    serviceRequestFormData.serviceRequestType === 'Common Area'
  );
  const { disableButton } = state;

  return (
    <Form
      headline={`Submit ${variables.serviceRequest}`}
      strapline="Include details about what's happening and we'll get back to you to confirm a service time."
      onClose={() =>
        serviceRequestFormData.faq_id
          ? onSetServiceRequestFormPreviousStep()
          : serviceRequestFormData.category
            ? onSetServiceRequestFormStep(4)
            : itemFaqCategories.length > 0
              ? onSetServiceRequestFormStep(3)
              : onSetServiceRequestFormStep(2)
      }
      onExit={() => {
        onExitServiceRequestForm();
        if (onExit) onExit();
      }}
      component={
        <ServiceRequestFormContent
          serviceRequestFormData={serviceRequestFormData}
          property={property}
          suite={suite}
          serviceRequestTags={serviceRequestTags}
          theme={theme}
          srAttachments={srAttachments}
          showTimeslots={showTimeslots}
          onSetServiceRequestFormData={onSetServiceRequestFormData}
          setTags={setTags}
          selectMultipleItems={selectMultipleItems}
          handleDelete={handleDelete}
          tenantName={tenantName}
          userRole={userRole}
        />
      }
      footer={
        <ServiceRequestFormFooter
          serviceRequestFormData={serviceRequestFormData}
          disableButton={disableButton}
          theme={theme}
          tenantName={tenantName}
          userRole={userRole}
          onSave={onSave}
          loader={loader}
          state={state}
          setState={setState}
          onSaveImagesAnotated={onSaveImagesAnotated}
        />
      }
      actions={undefined}
      onSubmit={undefined}
    />
  );
};

export default ServiceRequestFormDetails;
